/**
 * @file The cross-source Ask resolver — keyword-routed prose answers
 *       synthesized from the live source data the Dashboard already
 *       holds (ACCOUNT/COURSES/SECRETS), with an honest "no data" gap
 *       block naming the sources that aren't wired yet.
 * @module @labcraft/admin/ui/dashboard/ask
 *
 * There is no model backend behind this — rather than fake a model
 * call, the Ask reasons over the same client-side contributions the
 * boards render and is explicit about its blind spots. Pure logic;
 * unit-tested without a DOM.
 */

/** Numbers the Ask reasons over, computed by the panel from live data. */
export type AskContext = {
  learnersTotal: number
  awaiting: number
  coursesTotal: number
  coursesEnabled: number
  secretsStale: number
  needsCount: number
  liveCount: number
  sourcesTotal: number
}

/** One run of inline prose; `cite` indexes into {@link AskAnswer.cites}. */
export type AskBodyPart = { text: string; cite?: number }

/** A source citation chip rendered in the answer's side rail. */
export type AskCite = { n: number; src: string }

/** A resolved Ask answer: prose body + citations + an honest gap. */
export type AskAnswer = {
  body: AskBodyPart[]
  cites: AskCite[]
  gap: string
}

/** The keyword buckets the Ask routes a free-text query into. */
export type AskKind = 'needs' | 'funnel' | 'connected' | 'fallback'

/** The cross-source Ask — keyword routing + prose synthesis. */
export class DashboardAsk {
  /** The unconnected-source disclaimer shared across answers. */
  static readonly BILLING_GAP =
    "Billing isn't connected, so revenue, failed charges and churn have no source yet."

  /**
   * Route a free-text query to an answer bucket by keyword.
   *
   * @param query - The operator's question.
   * @returns The matched {@link AskKind}.
   */
  static route(query: string): AskKind {
    const s = query.toLowerCase()
    if (/funnel|convert|signup|sign up|growth/.test(s)) return 'funnel'
    if (/need|me|today|broke|wrong|do/.test(s)) return 'needs'
    if (/connect|source|wired|live/.test(s)) return 'connected'
    return 'fallback'
  }

  /**
   * Resolve a query into a cited prose answer over the live sources.
   *
   * @param query - The operator's question.
   * @param ctx - The live counts the answer is built from.
   * @returns The answer, or null when the query is blank.
   */
  static resolve(query: string, ctx: AskContext): AskAnswer | null {
    if (query.trim().length === 0) return null
    switch (DashboardAsk.route(query)) {
      case 'needs':
        return {
          body: [
            {
              text: `${ctx.needsCount} thing${ctx.needsCount === 1 ? '' : 's'} need you right now`,
              cite: 1,
            },
            {
              text: `: ${ctx.awaiting} learner${ctx.awaiting === 1 ? '' : 's'} awaiting review and ${ctx.secretsStale} secret${ctx.secretsStale === 1 ? '' : 's'} to rotate`,
              cite: 2,
            },
            { text: '.' },
          ],
          cites: [
            { n: 1, src: 'dashboard · needs-you' },
            { n: 2, src: 'account + secrets' },
          ],
          gap: DashboardAsk.BILLING_GAP,
        }
      case 'funnel':
        return {
          body: [
            {
              text: `${ctx.learnersTotal} learner${ctx.learnersTotal === 1 ? '' : 's'} in the funnel, ${ctx.awaiting} awaiting activation`,
              cite: 1,
            },
            {
              text: `, across ${ctx.coursesEnabled} of ${ctx.coursesTotal} enabled course${ctx.coursesTotal === 1 ? '' : 's'}`,
              cite: 2,
            },
            { text: '.' },
          ],
          cites: [
            { n: 1, src: 'account · learners' },
            { n: 2, src: 'courses · installed' },
          ],
          gap: "I can't show signup → paid — Billing isn't connected, so there's no revenue or churn signal yet.",
        }
      case 'connected':
        return {
          body: [
            {
              text: `${ctx.liveCount} of ${ctx.sourcesTotal} sources are live`,
              cite: 1,
            },
            {
              text: `. Hypervisor, Email and Billing aren't wired in yet`,
            },
            { text: '.' },
          ],
          cites: [{ n: 1, src: 'dashboard · sources board' }],
          gap: DashboardAsk.BILLING_GAP,
        }
      case 'fallback':
      default:
        return {
          body: [
            {
              text: 'I reason over the live sources — account, courses and secrets. Ask about the funnel, what needs you, or what is connected.',
            },
          ],
          cites: [],
          gap: 'I answer from the live boards you see — account, courses and secrets; deeper cross-source synthesis isn’t wired yet.',
        }
    }
  }
}
